/**
 * Well-known key under which {@link annotateError} stashes structured context
 * on an `Error`. `Symbol.for` keeps it collision-free and non-enumerable in
 * `JSON.stringify`, so it never leaks into serialized payloads by accident.
 */
export const ERROR_METADATA = Symbol.for('creator.bugsnag.errorMetadata');

/** A map of Bugsnag metadata section name to the key/value pairs in it. */
export type ErrorMetadata = Record<string, Record<string, unknown>>;

interface AnnotatedError {
	[ERROR_METADATA]?: ErrorMetadata;
}

/**
 * Attach a named section of structured context to an error so it survives to
 * Bugsnag even when the error bubbles *unhandled* — where there is no
 * `notify()` call site to add metadata at. The global `onError` handler in
 * `configureBugsnag()` reads this back with {@link getErrorMetadata} and
 * forwards each section via `event.addMetadata()`.
 *
 * Returns the same error so it can be thrown or rejected inline:
 *
 * ```ts
 * throw annotateError(new Error('Could not find photo model'), 'photoModel', { id });
 * ```
 *
 * Non-object errors (e.g. a rejected string) are returned untouched.
 *
 * @param error The error to annotate.
 * @param section Bugsnag metadata section name (a tab in the dashboard).
 * @param data Key/value context to attach. Merged into any existing section.
 * @returns The same error instance, annotated in place.
 */
export default function annotateError<E>(
	error: E,
	section: string,
	data: Record<string, unknown>,
): E {
	if (error === null || typeof error !== 'object') {
		return error;
	}

	const annotated = error as E & AnnotatedError;

	annotated[ERROR_METADATA] = {
		...annotated[ERROR_METADATA],
		[section]: {
			...annotated[ERROR_METADATA]?.[section],
			...data,
		},
	};

	return error;
}

/**
 * Read context previously attached with {@link annotateError}. Used by the
 * Bugsnag `onError` handler to forward it onto the reported event.
 *
 * @param error The (possibly annotated) error, e.g. `event.originalError`.
 * @returns The attached metadata, or `undefined` if there is none.
 */
export function getErrorMetadata(error: unknown): ErrorMetadata | undefined {
	if (error === null || typeof error !== 'object') {
		return undefined;
	}

	return (error as AnnotatedError)[ERROR_METADATA];
}
